<script lang="ts">
import type { StudySpendTrajectorySlideProps } from '$lib/components/study/slide-layout-types'
import StudySlideStatTileCard from '../cards/StudySlideStatTileCard.svelte'
import LineChart from '../charts/LineChart.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/**
 * Spend-trajectory slide (`s2b`): `StudySlide` → two-column `.tc` with line chart + stats + timeline.
 * Copy props use `{@html}` where noted — trusted study content only.
 */
let props: StudySpendTrajectorySlideProps = $props()

const statCols = $derived(props.statGridColumns ?? 2)
const chartAfterStyle = $derived(
  props.chartAfterStyle ?? 'margin:.5rem 0 0;font-size:.78rem;line-height:1.42;',
)
const rightIntroStyle = $derived(props.rightIntroStyle ?? 'margin-bottom:.9rem;')
const signalOnRight = $derived(props.signalPlacement === 'right')
const signalStyle = $derived(
  props.signalStyle ?? (signalOnRight ? 'margin-top:.72rem;' : 'margin-top:0;'),
)
const tcStyle = $derived(props.tcStyle ?? 'gap:1.4rem;align-items:start;')
/**
 * Every other slide type defaults a bottom margin on `.hl`; this one passed `headlineStyle`
 * straight through, so a caller that omits it got ZERO margin. `.hl` runs `line-height: 1.13` on a
 * display serif, so descenders overflow the line box and collide with the card directly below.
 * Every existing caller sets `headlineStyle` explicitly and is unaffected by this default.
 */
const headlineStyle = $derived(props.headlineStyle ?? 'margin-bottom:.9rem;')
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey" style={props.eyebrowStyle}>{@html props.eyebrow}</div>
    <div class="hl" style={headlineStyle}>{@html props.headline}</div>
    {#snippet signalCallout()}
      <div class="hb study-spend-trajectory-slide__signal" style={signalStyle}>
        <p class="study-spend-trajectory-slide__signal-p">
          <strong>The signal:</strong>
          {' '}{props.signal}
        </p>
      </div>
    {/snippet}
    <div class="tc" style={tcStyle}>
      <div style="display:flex;flex-direction:column;gap:.72rem;">
        <div class="cc">
          <div class="ct">{@html props.chartTitle}</div>
          <div class="cs">{@html props.chartSubtitle}</div>
          <LineChart
            active={props.active ?? false}
            labels={props.lineChart.labels}
            datasets={props.lineChart.datasets}
            height={props.lineChart.height ?? '210px'}
            yTickFormat={props.lineChart.yTickFormat}
            tooltipFormat={props.lineChart.tooltipFormat}
            maxTicksLimit={props.lineChart.maxTicksLimit}
            eventMarkers={props.lineChart.eventMarkers}
          />
          {#if props.legendRows?.length}
            <div class="lg">
              {#each props.legendRows as row}
                <div class="ld">
                  <div class="ldd" style={row.swatchStyle}></div>
                  {row.label}
                </div>
              {/each}
            </div>
          {/if}
          {#if props.chartAfterHtml}
            <p class="bd" style={chartAfterStyle}>{@html props.chartAfterHtml}</p>
          {/if}
        </div>
        <div style="display:grid;grid-template-columns:repeat({statCols}, 1fr);gap:.62rem;">
          {#each props.statTiles as tile}
            <StudySlideStatTileCard {...tile} />
          {/each}
        </div>
        {#if props.signal && !signalOnRight}
          {@render signalCallout()}
        {/if}
      </div>
      <div>
        {#if props.rightIntroHtml}
          <p class="bd" style={rightIntroStyle}>{@html props.rightIntroHtml}</p>
        {/if}
        {#each props.timelineItems as item}
          <div class="tli">
            <div class="ty">{item.year}</div>
            <div class="tt">{@html item.body}</div>
          </div>
        {/each}
        {#if props.signal && signalOnRight}
          {@render signalCallout()}
        {/if}
      </div>
    </div>
  </StudySlideContent>
</StudySlide>

<style>
.study-spend-trajectory-slide__signal-p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
}
</style>
